import type {UnwrapRefCarouselApi as CarouselApi, CarouselEmits, CarouselProps} from './interface'
import {createInjectionState} from '@vueuse/core'
import emblaCarouselVue from 'embla-carousel-vue'
import {nextTick, onBeforeUnmount, onMounted, type Ref, ref, watch} from 'vue'

const [useProvideCarousel, useInjectCarousel] = createInjectionState(
    ({
         opts,
         orientation,
         plugins,
         arrowColor,
         hoverArrowColor,
         outline,
         outlineColor,
     }: CarouselProps, emits: CarouselEmits) => {
        const [emblaNode, emblaApi] = emblaCarouselVue({
            ...opts,
            axis: orientation === 'horizontal' ? 'x' : 'y',
            inViewThreshold: 0.75, // Need to be 75% in view to count as visible.
        }, plugins)

        const currentIndex: Ref<number> = ref(1);
        const slideCount: Ref<number> = ref(1);

        function scrollTo(number) {
            emblaApi.value?.scrollTo(number);
        }

        function scrollPrev() {
            emblaApi.value?.scrollPrev()
        }

        function scrollNext() {
            emblaApi.value?.scrollNext()
        }

        const canScrollNext = ref(false)
        const canScrollPrev = ref(false)
        const allItemsVisible = ref(false)

        function onSelect(api: CarouselApi) {
            currentIndex.value = api?.selectedScrollSnap() + 1;
            canScrollNext.value = api?.canScrollNext() || false
            canScrollPrev.value = api?.canScrollPrev() || false

            setTimeout(() => {
                allItemsVisible.value = api?.slidesNotInView().length <= 0

                if (allItemsVisible.value) {
                    api?.reInit({
                        active: true
                    })
                }
            }, 100)
        }

        function setCount(count) {
            slideCount.value = count;
        }

        onMounted(() => {
            if (!emblaApi.value)
                return

            emblaApi.value?.on('init', onSelect)
            emblaApi.value?.on('reInit', onSelect)
            emblaApi.value?.on('select', onSelect)

            emits('init-api', emblaApi.value)
        })

        return {
            carouselRef: emblaNode,
            carouselApi: emblaApi,
            canScrollPrev,
            canScrollNext,
            allItemsVisible,
            setCount,
            scrollPrev,
            scrollNext,
            scrollTo,
            orientation,
            arrowColor,
            hoverArrowColor,
            outline,
            outlineColor,
            currentIndex
        }
    },
)

function useCarousel() {
    const carouselState = useInjectCarousel()

    if (!carouselState)
        throw new Error('useCarousel must be used within a <Carousel />')

    return carouselState
}

export {useCarousel, useProvideCarousel}
